import { Box, Divider, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SelectsDates, ToolsDetails } from '../../components/custom';
import { LayoutBase } from '../../layouts';
import { useNavigate, useParams } from 'react-router-dom';
import { IMantenance, MaintenancesServices } from '../../services/api/maintenances/MaintenancesServices';
import { ConfirmationDialog, CustomAlert } from '../../components/shared';
import { VAutoCompletEmployer } from '../../components/shared/VAutoCompletEmployer';
import { useAuthContext } from '../../context';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const MaintenancePlanningDetails = () => {

    const navigate = useNavigate();
    const { id, maintenance } = useParams();
    const { userData } = useAuthContext();
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [dates, setDates] = useState<string>();
    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [data, setData] = useState<IMantenance>({
        descricao: '',
        nome: '',
        frequencia: '',
        localTrabalho: '',
        tempo: '',
        responsavel: '',
        datas: '',
    });

    const pagePermissionsString = useMemo(() => {
        return userData?.nivel_acesso?.acompanhamentos?.comunicacao;
    }, [userData]);

    const hasActionPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
        if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
            const actions = pagePermissionsString.split(',').map(s => s.trim());
            return actions.includes(action);
        }
        return false;
    }, [pagePermissionsString]);

    useEffect(() => {
        if (maintenance !== 'novo') {
            fetchData();
        }
    }, [maintenance]);

    const fetchData = () => {
        if (maintenance && maintenance !== 'novo') {
            MaintenancesServices.get(maintenance)
                .then((response) => {
                    if (response instanceof Error) {
                        setAlertInfo({
                            open: true,
                            message: `Erro ao buscar registro. Erro: ${response.message}`,
                            severity: 'error'
                        });
                    } else {
                        setData(response);
                    }
                })
                .catch((error) => {
                    setAlertInfo({
                        open: true,
                        message: `Erro ao buscar registro. Erro: ${error.message}`,
                        severity: 'error'
                    });
                });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSelect = (event: SelectChangeEvent) => {
        setData(prevData => ({
            ...prevData,
            frequencia: event.target.value
        }));
    };

    function handleUserChange(user: string) {
        setData(prevData => ({
            ...prevData,
            responsavel: user
        }));
    }

    function verify(data: IMantenance): boolean {
        return (
            data.localTrabalho !== '' &&
            data.nome !== '' &&
            data.responsavel !== '' &&
            data.descricao !== '' &&
            data.tempo !== '' &&
            data.frequencia !== ''
        );
    }

    const handleSave = () => {
        const isEditing = maintenance !== 'novo';
        const hasAddPermission = hasActionPermission('add');
        const hasEditPermission = hasActionPermission('edit');
        if ((!isEditing && !hasAddPermission) || (isEditing && !hasEditPermission)) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para realizar esta ação.', severity: 'error' });
            return;
        }
        const valid = verify(data);
        valid == true ? setError(false) : setError(true);
        if (valid && id !== undefined && maintenance === 'novo') {
            data.id_barragem = id;
            MaintenancesServices.create(data)
                .then((response) => {
                    if (response instanceof Error) {
                        console.log(response.message);
                        setAlertInfo({ open: true, message: `Erro ao salvar registro! error:${response.message}`, severity: 'error' });
                    } else {
                        console.log(response);
                        setAlertInfo({ open: true, message: 'Registro salvo', severity: 'success' });
                    }
                });
        }

        if (valid && id !== undefined && maintenance !== 'novo' && maintenance) {
            MaintenancesServices.upgrade(maintenance, data)
                .then((response) => {
                    if (response instanceof Error) {
                        console.log(response.message);
                        setAlertInfo({ open: true, message: `Erro ao atualizado registro! error:${response.message}`, severity: 'error' });
                    } else {
                        console.log(response);
                        setAlertInfo({ open: true, message: 'Registro atualizado', severity: 'success' });
                    }
                });
        }
    };

    const handleOpenDeleteDialog = () => {
        if (!hasActionPermission('remove')) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para deletar este registro.', severity: 'error' });
            return;
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleDelete = () => {
        if (!hasActionPermission('remove')) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para deletar este registro.', severity: 'error' });
            return;
        }
        if (maintenance && maintenance !== 'novo') {
            MaintenancesServices.delet(maintenance)
                .then((response) => {
                    if (response instanceof Error) {
                        setAlertInfo({ open: true, message: `Erro ao buscar registro! error:${response.message}`, severity: 'error' });
                    } else {
                        setAlertInfo({ open: true, message: 'Registro excluido!', severity: 'success' });
                        navigate(`/information_dam/${id}/maintenance_planning`);
                    }
                });
        }
        handleCloseDialog();
    };

    const handleDateChange = (value: string) => {
        setDates(value);
    };

    useEffect(() => {
        if (dates) {
            data.datas = dates;
        }
    }, [dates]);

    const handleSelectDates = (event: SelectChangeEvent) => {
        setData(prevData => ({
            ...prevData,
            datas: event.target.value
        }));
    };

    const handleChangeDate = (date: Dayjs | null) => {
        setData(prevData => ({
            ...prevData,
            datas: date ? date.format('YYYY-MM-DD') : '',
        }));
    };

    return (
        <LayoutBase
            title='Cadastro de Manutenção'
            tollBar={
                <ToolsDetails
                    onClickNew={() => navigate('./novo')}
                    onClickSave={() => handleSave()}
                    onClickDelet={() => handleOpenDeleteDialog()}
                    showDeletButton={maintenance !== 'novo' && hasActionPermission('remove')}
                    showSaveButton={maintenance == 'novo' ? hasActionPermission('add') : hasActionPermission('edit')}
                    showNewButton={hasActionPermission('add')}
                />
            }
        >
            <Box padding={2}>
                <Box marginBottom={2}>
                    <Typography>Ficha de manutenção</Typography>
                    <Divider />
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={smDown ? 12 : 6}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            label='Nome da Atividade'
                            fullWidth
                            name='nome'
                            value={data.nome}
                            onChange={handleChange}
                            error={error && data.nome === ''}
                            helperText={error && data.nome === '' ? 'É necessario preencher este campo' : null}
                            required
                        />
                    </Grid>
                    <Grid item xs={smDown ? 12 : 6}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            label='Tempo de execução (Dias)'
                            fullWidth
                            type='number'
                            name='tempo'
                            value={data.tempo}
                            onChange={handleChange}
                            error={error && data.tempo === ''}
                            helperText={error && data.tempo === '' ? 'É necessario preencher este campo' : null}
                            required
                        />
                    </Grid>
                    <Grid item xs={smDown ? 12 : 6}>
                        {userData.id_empresa !== undefined ?
                            <VAutoCompletEmployer uuidCompany={userData.id_empresa} onUserChange={(user) => handleUserChange(user)} error={error && data.responsavel === ''} value={data.responsavel} required />
                            : null}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            label='Descrição da atividade'
                            fullWidth
                            multiline
                            maxRows={3}
                            value={data.descricao}
                            name='descricao'
                            onChange={handleChange}
                            error={error && data.descricao === ''}
                            helperText={error && data.descricao === '' ? 'É necessario preencher este campo' : null}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            label='Local de trabalho'
                            fullWidth
                            multiline
                            maxRows={3}
                            value={data.localTrabalho}
                            name='localTrabalho'
                            onChange={handleChange}
                            error={error && data.localTrabalho === ''}
                            helperText={error && data.localTrabalho === '' ? 'É necessario preencher este campo' : null}
                            required
                        />
                    </Grid>
                    <Grid item xs={smDown ? 12 : 6}>
                        <FormControl fullWidth>
                            <InputLabel id="frequencia">Recorrência *</InputLabel>
                            <Select
                                labelId="frequencia"
                                label="Recorrência"
                                fullWidth
                                error={error && data.frequencia === ''}
                                value={data.frequencia}
                                onChange={handleSelect}
                                required
                            >
                                <MenuItem value={'unica'}>Execução Única</MenuItem>
                                <MenuItem value={'semanal'}>Semanal</MenuItem>
                                <MenuItem value={'quinzenal'}>Quinzenal</MenuItem>
                                <MenuItem value={'mensal'}>Mensal</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={smDown ? 12 : 6}>
                        {data.frequencia === 'unica' ?
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                <DemoContainer sx={{ marginTop: '-0.5rem', width: '100%' }} components={['DatePicker']}>
                                    <DatePicker
                                        label="Dia de execução"
                                        name='datas'
                                        onChange={handleChangeDate}
                                        format="DD/MM/YYYY"
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                            : data.frequencia === 'quinzenal' ?
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                    <DemoContainer sx={{ marginTop: '-0.5rem', width: '100%' }} components={['DatePicker']}>
                                        <DatePicker
                                            label="Dia de execução quinzenal"
                                            name='datas'
                                            onChange={handleChangeDate}
                                            format="DD/MM/YYYY"
                                        />
                                    </DemoContainer>
                                </LocalizationProvider>
                                : data.frequencia === 'semanal' ?
                                    <FormControl fullWidth>
                                        <InputLabel id="frequencia">Dia da semana</InputLabel>
                                        <Select
                                            label="Dia da semana"
                                            fullWidth
                                            name='datas'
                                            onChange={handleSelectDates}
                                        >
                                            <MenuItem value={'segunda'}>Segunda-feira</MenuItem>
                                            <MenuItem value={'terca'}>Terça-feira</MenuItem>
                                            <MenuItem value={'quarta'}>Quarta-feira</MenuItem>
                                            <MenuItem value={'quinta'}>Quinta-feira</MenuItem>
                                            <MenuItem value={'sexta'}>Sexta-feira</MenuItem>
                                            <MenuItem value={'sabado'}>Sábado</MenuItem>
                                            <MenuItem value={'domingo'}>Domingo</MenuItem>
                                        </Select>
                                    </FormControl>
                                    : data.frequencia === 'mensal' ?
                                        <SelectsDates onChangeDates={(value) => handleDateChange(value)} />
                                        : null}
                    </Grid>
                </Grid>
            </Box>
            {alertInfo.open &&
                <CustomAlert
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            }
            <ConfirmationDialog
                isOpen={dialogOpen}
                onClose={handleCloseDialog}
                onConfirm={handleDelete}
                title="Confirmação de Exclusão"
                message="Tem certeza que deseja excluir este Monitoramento Topobatimétrico?"
            />
        </LayoutBase>
    );
};